import { useEffect, useState } from "react";
import { Gauge as GaugeIcon, TrendingUp, Info, CheckCircle2, AlertTriangle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/common";

const bandOf = (s) => (s >= 750 ? "Excellent" : s >= 700 ? "Good" : s >= 650 ? "Fair" : "Needs Work");
const colorOf = (s) => (s >= 750 ? "#14B8A6" : s >= 700 ? "#0D9488" : s >= 650 ? "#F59E0B" : "#EF4444");
const BANDS = [
  { label: "Needs Work", min: 300, color: "#EF4444" },
  { label: "Fair", min: 650, color: "#F59E0B" },
  { label: "Good", min: 700, color: "#0D9488" },
  { label: "Excellent", min: 750, color: "#14B8A6" },
];

function GaugeView({ score, c, pct }) {
  const R = 90, cx = 110, cy = 110;
  const theta = Math.PI * (1 - pct / 100);
  const x = cx + R * Math.cos(theta), y = cy - R * Math.sin(theta);
  return (
    <div className="grid place-items-center" data-testid="cs-gauge">
      <svg viewBox="0 0 220 128" className="w-full max-w-[260px]">
        <path d="M20 110 A90 90 0 0 1 200 110" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="16" strokeLinecap="round" />
        <path d={`M20 110 A90 90 0 0 1 ${x.toFixed(1)} ${y.toFixed(1)}`} fill="none" stroke={c} strokeWidth="16" strokeLinecap="round" style={{ transition: "all 1s ease" }} />
        <circle cx={x} cy={y} r="7" fill="#fff" stroke={c} strokeWidth="3" />
      </svg>
      <div className="-mt-6 text-center">
        <p className="font-display text-5xl font-bold" style={{ color: c }}>{score}</p>
        <p className="text-sm font-semibold" style={{ color: c }}>{bandOf(score)}</p>
        <p className="text-[10px] text-slate-400">Range 300–900</p>
      </div>
    </div>
  );
}

function BandView({ score, c }) {
  return (
    <div className="w-full max-w-md space-y-3" data-testid="cs-band">
      <div className="text-center"><p className="font-display text-5xl font-bold" style={{ color: c }}>{score}</p><p className="text-sm font-semibold" style={{ color: c }}>{bandOf(score)}</p></div>
      <div className="space-y-2">
        {BANDS.map((b) => {
          const active = bandOf(score) === b.label;
          return (
            <div key={b.label} className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm ${active ? "text-white" : "text-slate-300"}`} style={{ background: active ? b.color : "rgba(255,255,255,0.06)" }}>
              <span className="font-semibold">{b.label}</span>
              <span className="text-xs opacity-90">{b.min}+{active ? "  ● you" : ""}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function CreditScore() {
  const [cs, setCs] = useState(null);
  const [view, setView] = useState("both");
  useEffect(() => { api.get("/credit-score").then((r) => setCs(r.data)).catch(() => setCs({})); }, []);
  if (!cs) return <div className="space-y-4"><Skeleton className="h-48" /><Skeleton className="h-64" /></div>;

  const hasScore = typeof cs.score === "number";
  const c = hasScore ? colorOf(cs.score) : "#64748b";
  const pct = hasScore ? ((cs.score - 300) / 600) * 100 : 0;
  const tips = [
    "Pay every EMI on or before the due date to build a strong history.",
    "Keep your credit utilisation below 30% of available limits.",
    "Avoid multiple new loan applications in a short period.",
    "Review your report regularly for errors and dispute them.",
  ];

  return (
    <div className="space-y-6" data-testid="credit-score-page">
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-2xl font-bold text-[#0B132B]">Credit Score</h1><p className="text-sm text-slate-500">Understand and improve your credit health.</p></div>
        <span className="rounded-md bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700" data-testid="cs-demo-badge">DEMO / SAMPLE DATA</span>
      </div>

      {!hasScore ? (
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-6 text-slate-500" data-testid="cs-unavailable">
          <AlertTriangle size={18} className="text-slate-400" /> Credit Score: Not available from lender
        </div>
      ) : (
        <div className="rounded-2xl navy-grain p-6 text-white">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm text-slate-300"><GaugeIcon size={16} className="text-[#14B8A6]" /> Your score (sample)</div>
            <div className="flex gap-1 rounded-lg bg-white/10 p-1" data-testid="cs-view-toggle">
              {["gauge", "band", "both"].map((v) => (
                <button key={v} data-testid={`cs-view-${v}`} onClick={() => setView(v)} className={`rounded-md px-3 py-1 text-xs font-semibold capitalize transition-colors ${view === v ? "bg-[#14B8A6] text-white" : "text-slate-300 hover:text-white"}`}>{v}</button>
              ))}
            </div>
          </div>
          <div className={`flex flex-col items-center gap-8 ${view === "both" ? "sm:flex-row sm:justify-around" : ""}`}>
            {(view === "gauge" || view === "both") && <GaugeView score={cs.score} c={c} pct={pct} />}
            {(view === "band" || view === "both") && <BandView score={cs.score} c={c} />}
          </div>
          <p className="mt-5 flex items-center gap-1.5 rounded-md bg-white/5 px-3 py-2 text-xs text-slate-300"><Info size={13} /> This is DEMO / SAMPLE data. A licensed credit bureau (e.g. CIBIL/Experian) or your lender will power real scores once integrated. ZRo Pe never fabricates a real credit score.</p>
        </div>
      )}

      {hasScore && cs.history && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="flex items-center gap-2 font-display font-bold text-[#0B132B]"><TrendingUp size={16} className="text-[#14B8A6]" /> Score History <span className="text-[10px] font-semibold text-amber-600">(DEMO)</span></h3>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 300, height: 200 }}>
              <LineChart data={cs.history} margin={{ left: -20, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} />
                <YAxis domain={[600, 800]} tick={{ fontSize: 12, fill: "#64748b" }} />
                <Tooltip />
                <Line type="monotone" dataKey="score" stroke="#14B8A6" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="font-display font-bold text-[#0B132B]">How to improve your score</h3>
        <div className="mt-3 space-y-2">
          {tips.map((t) => (<div key={t} className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle2 size={16} className="mt-0.5 shrink-0 text-[#14B8A6]" /> {t}</div>))}
        </div>
      </div>
    </div>
  );
}
